var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
const styleElement = document.createElement('style');
styleElement.innerHTML = '.react-managed-draggable-unselectable { user-select: none; }';
document.body.appendChild(styleElement);
const rafThrottle = (callback) => {
    let requestId;
    const later = (context, args) => () => {
        requestId = null;
        callback.apply(context, args);
    };
    const throttled = function (...args) {
        if ((requestId === null) || (requestId === undefined)) {
            requestId = window.requestAnimationFrame(later(this, args));
        }
    };
    throttled.cancel = () => requestId !== null && window.cancelAnimationFrame(requestId);
    return throttled;
};
function manhattanDistance(p1, p2) {
    return Math.abs(p1.x - p2.x) + Math.abs(p1.y - p2.y);
}
export function getPosition(event) {
    if ('touches' in event) {
        if (event.touches.length >= 1) {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
        else {
            return { x: 0, y: 0 };
        }
    }
    else {
        return { x: event.clientX, y: event.clientY };
    }
}
export class ElementDraggable {
    constructor(element, options, immediate) {
        this.down = false;
        this.dragging = false;
        this.start = { x: 0, y: 0 };
        this.current = { x: 0, y: 0 };
        this.handleDown = (event) => {
            if (!this.down && !this.dragging) {
                this.down = true;
                document.addEventListener('mousemove', this.handleMove);
                document.addEventListener('touchmove', this.handleMove);
                document.addEventListener('mouseup', this.handleUp);
                document.addEventListener('touchend', this.handleUp);
                document.body.classList.add('react-managed-draggable-unselectable');
                this.current = this.start = getPosition(event);
                if (this.getThreshold() <= 0) {
                    this.dragging = true;
                    if (this.options.onDragStart) {
                        this.options.onDragStart(event, this.generateDragInformation(this.current));
                    }
                }
                event.stopPropagation();
                event.preventDefault();
            }
        };
        this.handleMove = rafThrottle((event) => {
            if (this.down || this.dragging) {
                const last = this.current;
                this.current = getPosition(event);
                if (this.dragging) {
                    if (this.options.onDragMove) {
                        this.options.onDragMove(event, this.generateDragInformation(last));
                    }
                }
                else {
                    if (manhattanDistance(this.start, this.current) >= this.getThreshold()) {
                        this.dragging = true;
                        if (this.options.onDragStart) {
                            this.options.onDragStart(event, this.generateDragInformation(last));
                        }
                    }
                }
            }
        });
        this.handleUp = (event) => {
            if (this.down || this.dragging) {
                this.down = false;
                document.body.classList.remove('react-managed-draggable-unselectable');
                document.removeEventListener('mousemove', this.handleMove);
                document.removeEventListener('touchmove', this.handleMove);
                document.removeEventListener('mouseup', this.handleUp);
                document.removeEventListener('touchend', this.handleUp);
                if (this.dragging) {
                    this.dragging = false;
                    const last = this.current;
                    // If touch is released, there are no event coordinates
                    // this.current = getPosition(event);
                    if (this.options.onDragEnd) {
                        this.options.onDragEnd(event, this.generateDragInformation(last));
                    }
                }
                else if (this.down && this.options.onClick) {
                    if ('touches' in event || event.button === 0) {
                        const last = this.current;
                        if (!('touches' in event)) {
                            this.current = getPosition(event);
                        }
                        this.options.onClick(event, this.generateDragInformation(last));
                    }
                }
            }
        };
        this.element = element;
        this.options = options;
        this.immediate = !!immediate;
        if (immediate) {
            this.dragging = true;
            document.addEventListener('mousemove', this.handleMove);
            document.addEventListener('touchmove', this.handleMove);
            document.addEventListener('mouseup', this.handleUp);
            document.addEventListener('touchend', this.handleUp);
        }
        else {
            element.addEventListener('mousedown', this.handleDown);
            element.addEventListener('touchstart', this.handleDown);
        }
    }
    destroy() {
        if (!this.immediate) {
            this.element.removeEventListener('mousedown', this.handleDown);
            this.element.removeEventListener('touchstart', this.handleDown);
        }
        if (this.down) {
            this.down = false;
            if (this.options.onDragEnd) {
                this.options.onDragEnd(undefined, this.generateDragInformation(this.current));
            }
            document.removeEventListener('mousemove', this.handleMove);
            document.removeEventListener('touchmove', this.handleMove);
            document.removeEventListener('mouseup', this.handleUp);
            document.removeEventListener('touchend', this.handleUp);
        }
    }
    getThreshold() {
        return this.options.threshold === undefined ? 0 : this.options.threshold;
    }
    generateDragInformation(last) {
        return {
            current: this.current,
            start: this.start,
            last
        };
    }
}
export class Draggable extends React.Component {
    constructor() {
        super(...arguments);
        this.element = null;
        this.draggable = null;
    }
    componentDidMount() {
        if (this.element !== null) {
            this.draggable = new ElementDraggable(this.element, {
                threshold: this.props.threshold,
                onDragStart: this.props.onDragStart,
                onDragMove: this.props.onDragMove,
                onDragEnd: this.props.onDragEnd,
                onClick: this.props.onClick
            });
        }
    }
    componentWillUnmount() {
        if (this.draggable !== null) {
            this.draggable.destroy();
            this.draggable = null;
        }
    }
    render() {
        const _a = this.props, { children, threshold, onDragStart, onDragMove, onDragEnd, onClick } = _a, other = __rest(_a, ["children", "threshold", "onDragStart", "onDragMove", "onDragEnd", "onClick"]);
        return React.createElement("div", Object.assign({ ref: (ref) => this.element = ref }, other), children);
    }
}
//# sourceMappingURL=index.js.map